<template>
  <div class="asset_section">
    <div class="search_upload">
      <div class="input_container">
        <div v-show="search" class="clear" @click="search = ''">
          <svg-icon
            ref="icon"
            :width="'16'"
            :height="'16'"
            icon="app-icons/close"
            fill="#fff"
          />
        </div>
        <input
          v-model.trim="search"
          :spellcheck="false"
          class="search"
          placeholder="Search assets..."
        />
      </div>
      <!-- TEMPORARILY -->
      <label for="asset-file-input" class="button"> Upload asset </label>
      <asset-file-uploader
        id="asset-file-input"
        :init-asset-id="container"
        :accept-messages="false"
      />
      <!-- /TEMPORARILY -->
    </div>
    <div v-if="search" class="breadcrumb">
      <span class="name current"> Search Results </span>
    </div>
    <div v-else class="breadcrumb">
      <span
        class="name"
        :class="{ current: container === getRootContainer.id }"
        @click="onClickRoot"
      >
        My Images
      </span>
      <div
        v-for="(item, index) in breadcrumb"
        :id="item.id"
        :key="item.id"
        class="level"
        :index="index"
      >
        <svg-icon
          ref="icon"
          :width="'8'"
          :height="'8'"
          icon="app-icons/arrow-down"
          fill="#787B7B"
        />
        <span
          class="name"
          :class="{ current: container === item.id }"
          @click="onClickBreadcrumb(index)"
        >
          {{ item.name }}
        </span>
      </div>
    </div>
    <div v-if="getAllNodes.length > 1" class="assetList">
      <asset-thumb
        v-for="(item, index) in containerAssets"
        :id="item.id"
        :key="item.id"
        :index="index"
        @click-event="onClick(item)"
      />
    </div>
    <div v-else class="empty">
      <h3>No assets yet</h3>
      Click on "Upload asset" to upload your first asset.
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import SvgIcon from '../SvgIcon.vue';
import AssetThumb from './AssetThumb.vue';
import { matchesHTML, getFileExtension, has } from '../../../utils.js';
import { EVENT } from '../../../constants.js';
import AssetFileUploader from '../../uploader/AssetFileUploader.vue';

export default {
  components: {
    SvgIcon,
    AssetThumb,
    AssetFileUploader,
  },

  props: {
    // The current asset that is being replaced (optional)
    assetReference: {
      type: Object,
      default: null,
    },
    // A callback to use when the user selects an asset
    onSelect: {
      type: Function,
      default: () => {},
    },
  },

  data() {
    return {
      search: '',
      breadcrumb: [],
    };
  },

  computed: {
    ...mapGetters('project/data/assets', [
      'getContainedNodes',
      'getRootContainer',
      'getIsRecursiveContainer',
      'getAllNodes',
    ]),

    /**
     * Return the id of the container that is currently selected
     */
    container() {
      if (this.search) return null;
      if (this.breadcrumb.length > 0) {
        return this.breadcrumb[this.breadcrumb.length - 1].id;
      }
      return this.getRootContainer.id;
    },

    /**
     * Return all the assets matching the search or are in the current container
     */
    containerAssets() {
      // If search is active show all items and containers matching the search term
      if (this.search) {
        return this.getAllNodes.filter(asset => {
          const { name, file } = asset;
          const extension = getFileExtension(file);
          const fullName = `${name}.${extension}`;
          return (
            name &&
            matchesHTML(this.search, fullName) &&
            (asset.type === 'image' ||
              asset.type === 'template-image' ||
              has(asset, 'children'))
          );
        });
      }
      // If not show the last folder in breadcrumb
      return this.getContainedNodes(
        this.container,
        'assets',
        'children'
      ).filter(
        asset =>
          asset.type === 'image' ||
          asset.type === 'template-image' ||
          has(asset, 'children')
      );
    },
  },

  methods: {
    /**
     * An asset or container was clicked
     */
    onClick(asset) {
      if (this.getIsRecursiveContainer(asset.id)) {
        this.breadcrumb.push(asset);
      } else {
        // Execute the select callback function
        this.onSelect({ id: asset.id });
      }
    },

    /**
     * Root of breadcrumb was clicked
     */
    onClickRoot() {
      this.search = '';
      this.breadcrumb = [];
    },

    /**
     * A breadcrumb item (non-root) was clicked
     */
    onClickBreadcrumb(index) {
      this.search = '';
      this.breadcrumb.splice(index + 1);
    },

    /**
     * Upload asset menu option is clicked [TEMPORARILY DISABLED]
     */
    // onClickUploadAsset() {
    //   emitter.emit(EVENT.OPEN_FILE_UPLOADER, this.container, false, false);
    // },
  },
};
</script>
<style scoped lang="scss">
.asset_section {
  padding: 15px;
  .search_upload {
    display: flex;
    margin: 0 0 8px 0;

    .input_container {
      flex: 3;
      position: relative;
      margin: 0 15px 0 0;

      .clear {
        position: absolute;
        opacity: 0.25;
        top: 12px;
        right: 14px;
        cursor: pointer;
        &:hover {
          opacity: 0.8;
        }
      }
      .search {
        width: 100%;
        height: 40px;
        background: #141616;
        border: none;
        border-radius: 4px;
        padding: 10px 14px 8px 14px;
        color: #fff;
        &:focus {
          background: #0a0a0a;
        }
      }
    }
  }
  .breadcrumb {
    color: #fff;
    display: flex;
    margin: 0 0 12px 0;
    > .name {
      margin-left: 0 !important;
    }
    .level {
      margin: 0 0 0 8px;
      display: flex;
    }
    .name {
      margin: 0 0 0 8px;
      color: #bcbcbc;
      cursor: pointer;
      padding: 0;
      font-size: 13px;
      &:hover {
        text-decoration: underline;
        color: #fff;
      }
      &.current {
        cursor: default;
        color: #fff;
        text-decoration: none;
        // font-weight: 400;
      }
    }
    .svg-icon-container {
      transform: rotate(-90deg);
    }
  }
  .button {
    padding: 0 14px;
    flex: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 3px;
    width: 100%;
    border: none;
    color: #fff;
    background: #db841e;
    cursor: pointer;
    margin: 0;
    text-align: center;
    position: relative;
    -webkit-transition: background 0.25s;
    transition: background 0.25s;
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
  }

  .assetList {
    display: grid; /* 1 */
    grid-template-columns: repeat(auto-fill, 31%); /* 2 */
    grid-gap: 10px; /* 3 */
    justify-content: space-between; /* 4 */
    overflow-y: auto;
    max-height: 238px;
    padding-right: 16px;
  }
  .empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 236px;
    flex-flow: column;
    h3 {
      margin: 0 0 10px 0;
    }
  }

  .assetList::-webkit-scrollbar-track {
    background-color: #1e2121;
    border: none;
    border-radius: 999px;
  }

  .assetList::-webkit-scrollbar {
    width: 8px;
    background-color: transparent;
  }

  .assetList::-webkit-scrollbar-thumb {
    width: 8px;
    background-color: #db841e;
    border-radius: 999px;
  }

  .assetList::-webkit-scrollbar-thumb:hover {
    background-color: #db841e;
  }
}
</style>
